/**
 * Types, interfaces, and constants for ProjectionInputView component
 * Extracted for maintainability and reuse across projection components
 */

import { MonthlyTotals } from '../../types';
import { UnitCountsData } from '../../services/api';
import { ProjectionInputRecord, ProjectionCalculatedRecord } from '../../types/projections';
import { ProjectionViewData } from '../../types/projectionViewData';
import type { DepartmentAggregates } from './SeasonalityModeView';
import type { MonthlyAdjustmentContext } from './seasonalityModeTypes';

/**
 * Account data from the hybrid API response
 */
export interface AccountData {
  account: string;
  description: string;
  ytdTotal: number;
  monthlyTotals: MonthlyTotals;
}

/**
 * DOC gross data from API
 */
export interface DOCGrossData {
  department: string;
  displayName: string;
  sales?: MonthlyTotals;
  cos?: MonthlyTotals;
  gross?: MonthlyTotals;
  ytdSales?: number;
  ytdCos?: number;
  ytdGross: number;
  monthlyGross?: MonthlyTotals;
  // Combined total gross for vehicle departments (front-end + F&I)
  totalGross?: MonthlyTotals;
  ytdTotalGross?: number;
  // Operating Profit fields (Gross - Expenses) for seasonality
  totalExpenses?: MonthlyTotals;
  operatingProfit?: MonthlyTotals;
  ytdTotalExpenses?: number;
  ytdOperatingProfit?: number;
}

/**
 * Historical year data bundle
 */
export interface HistoricalYearBundle {
  year: number;
  accounts: AccountData[];
  docGross: DOCGrossData[];
  unitCountsData: UnitCountsData | null;
  // Location-level Operating Profit for seasonality calculation
  locationOperatingProfit?: {
    ytdTotal: number;
    monthlyTotals: MonthlyTotals;
  };
}

/**
 * Pre-calculated expense account amounts for consolidated view
 * Key format: "dept#EXPENSE_accountCode" e.g., "NEW_VEHICLE#EXPENSE_011A"
 */
export interface ExpensesByAccount {
  [key: string]: {
    annualTotal: number;
    monthlyDistribution: Record<string, number>;
  };
}

/**
 * Props for ProjectionInputView component
 */
export interface ProjectionInputViewProps {
  locationName: string;
  locationId: number;
  targetYear: number;
  // Display years (ascending): 3, or 4 during the Oct-Dec window (incl. the partial prior year)
  historicalYears: number[];
  // Years driving the 50/30/20 seasonality weights, newest first
  seasonalityYears: number[];
  // True before Jan 10 of the target year (provisional monthly spread)
  seasonalityProvisional?: boolean;
  // Historical data keyed by year
  historicalDataByYear: Record<number, HistoricalYearBundle | null>;
  // Current projection inputs
  inputs: ProjectionInputRecord[];
  // Stored seasonality calculations from DynamoDB
  calculations?: ProjectionCalculatedRecord[];
  localInputs: Record<string, { value: number; inputType: 'amount' | 'percentage'; isDirty: boolean }>;
  // Edit state
  isEditing: boolean;
  onValueChange: (department: string, metric: string, value: number, inputType?: 'amount' | 'percentage') => void;
  // Formatting
  formatCurrency: (value: number) => string;
  // Brands available at this location (from historical data)
  availableBrands?: {
    newVehicle: string[];
    usedVehicle: string[];
  };
  // Callback to receive calculated aggregates from SeasonalityModeView
  onAggregatesCalculated?: (aggregates: DepartmentAggregates[]) => void;
  // Comparison mode: show actual data alongside projected
  comparisonMode?: boolean;
  // Actual data for the target year (when comparison mode is enabled)
  actualData?: HistoricalYearBundle | null;
  // Consolidated view (aggregated data, read-only)
  isConsolidated?: boolean;
  // Pre-calculated expense account amounts for consolidated view (exact sums from all locations)
  expensesByAccount?: ExpensesByAccount;
  // Executive monthly adjustments (Monthly Seasonality view)
  monthlyAdjustment?: MonthlyAdjustmentContext;
  // Pre-computed view data from backend (for simplified rendering)
  viewData?: ProjectionViewData;
  // Department assignment filtering for DepartmentManagers
  assignedDepartmentIds?: string[];
}

// Row color classes (matching DOCReportView)
export const ROW_COLORS = {
  sales: 'bg-blue-50 text-blue-900',
  gross: 'bg-green-50 text-green-900',
  fiIncome: 'bg-green-50 text-green-900',
  totalGross: 'bg-green-100 text-green-900',
  variableExpense: 'bg-orange-50 text-orange-900',
  semiFixedExpense: 'bg-amber-50 text-amber-900',
  salesExpense: 'bg-yellow-50 text-yellow-900',
  fixedExpense: 'bg-purple-50 text-purple-900',
  otherExpense: 'bg-gray-100 text-gray-900',
  totalExpense: 'bg-red-50 text-red-900',
  profit: 'bg-emerald-100 text-emerald-900',
  loss: 'bg-red-100 text-red-900',
  rentalIncome: 'bg-teal-50 text-teal-900',
  rentalExpense: 'bg-orange-50 text-orange-900',
  clearing: 'bg-gray-50 text-gray-900',
  totalMisc: 'bg-gray-200 text-gray-900',
};

// Map category names to colors
export const CATEGORY_COLORS: Record<string, string> = {
  'VARIABLE': ROW_COLORS.variableExpense,
  'SEMI_FIXED': ROW_COLORS.semiFixedExpense,
  'SALES': ROW_COLORS.salesExpense,
  'FIXED': ROW_COLORS.fixedExpense,
  'OTHERS': ROW_COLORS.otherExpense,
  'RENTAL_INCOME': ROW_COLORS.rentalIncome,
  'RENTAL_SELLING': ROW_COLORS.variableExpense,
  'RENTAL_FIXED': ROW_COLORS.fixedExpense,
  'CLEARING': ROW_COLORS.clearing,
};

/**
 * Income category names - credits in these categories should display as positive
 */
export const INCOME_CATEGORIES = ['RENTAL_INCOME', 'OTHER_INCOME'];

/**
 * Apply sign convention to account value
 * @param value - The raw account value
 * @param sign - Whether the account is credit or debit
 * @param isIncomeCategory - Whether this is an income category (RENTAL_INCOME, OTHER_INCOME)
 * @returns The value with proper sign applied
 */
export function applySign(value: number, sign: 'credit' | 'debit', isIncomeCategory: boolean): number {
  if (sign === 'credit') {
    if (isIncomeCategory) {
      return -value;
    } else {
      return value;
    }
  }
  return value;
}
